import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@core/components/toasts/toasts.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActionRequiredService } from 'services/action-required/action-required.service';
import { AuthService } from 'services/auth.service';
import { SystemModuleType } from 'services/enums/SystemModuleType.enum';
import { ActionRequiredModel } from 'services/models/action-required';
import { WorkforcePlanSolicitationDetailsModel } from 'services/models/workforceplan/workforceplan-solicitation-details.model';
import { WorkforcePlanSolicitationModel } from 'services/models/workforceplan/workforceplan-solicitation.model';
import { WorkforceplanService } from 'services/workforceplan/workforceplan-main.service';
import { TreatActionPlanDrawerComponent } from '../action-plan/treat-action-plan-drawer/treat-action-plan-drawer.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppSharedDrawerWorkforceplanSolicitationComponent } from '../drawer-workforce-plan-solicitation/workforce-plan-drawer-team-solicitation.component';

type QueryParams = {
  actionPlanId?: string;
};

@Component({
  selector: 'app-shared-action-required-list',
  templateUrl: './action-required-list.component.html',
  styleUrls: ['./action-required-list.component.scss'],
})
export class AppSharedActionRequiredListComponent {
  @Input() data: ActionRequiredModel[] = [];

  @Input() className?: string = '';

  @Input() action?: ActionRequiredModel;

  @Output() onListUpdated = new EventEmitter();

  @Output() onOpenDrawer = new EventEmitter();

  public listItems = [];
  public actionDetails: [number, ActionRequiredModel];
  public isLoadingDrawerData = false;

  private destroy$ = new Subject<void>();

  constructor(
    private _workforcePlanService: WorkforceplanService,
    private _toasterService: ToastService,
    private _authService: AuthService,
    private _modalService: NgbModal,
    private _activatedRoute: ActivatedRoute,
    private _actionRequired: ActionRequiredService,
    private _router: Router
  ) {}

  ngOnInit() {
    this._subscribeToQueryParams();
  }

  private _subscribeToQueryParams(): void {
    this._activatedRoute.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: QueryParams) => {
        const { actionPlanId } = params;

        if (actionPlanId) {
          this.onActionPlan(actionPlanId);
          this._clearQueryParams();
        }
      });
  }

  private _clearQueryParams(): void {
    setTimeout(() => {
      this._router.navigate([], {
        relativeTo: this._activatedRoute,
        queryParams: {},
        replaceUrl: true,
      });
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.action?.currentValue) {
      this.handleShowDetails(0, changes.action.currentValue);
    }
  }

  public handleOnCancel() {}

  public handleShowDetails(index: number, action: ActionRequiredModel) {
    this.actionDetails = [index, action];

    switch (action.source) {
      case SystemModuleType.WorforcePlan.value: {
        this.onActionWorkforcePlan(action.getUrl);
        break;
      }
      case SystemModuleType.WCK.value:
      case SystemModuleType.ETHICS_CHANNEL.value: {
        this.onActionPlan(action.referenceId);
        break;
      }
      case SystemModuleType.PROBATIONARY_PERIOD.value: {
        const url = new URL(action.getUrl);

        this._router.navigate([url.pathname], {
          queryParams: {
            employeeId: url.searchParams.get('employeeId'),
            relationId: url.searchParams.get('relationId'),
          },
        });

        break;
      }
      default: {
        break;
      }
    }
  }

  private async onActionPlan(actionPlanId: string): Promise<void> {
    this.isLoadingDrawerData = true;

    const _modalRef = this._modalService.open(TreatActionPlanDrawerComponent, {
      backdrop: 'static',
      keyboard: true,
      animation: false,
    });

    _modalRef.componentInstance.actionPlanId = actionPlanId;
    _modalRef.componentInstance.filterByEmployee = true;
    _modalRef.componentInstance.closeWhenFinished = true;

    this.isLoadingDrawerData = false;

    _modalRef.result.then(async response => {
      if (response?.onRefetch) {
        this.onListUpdated.emit();
      }
    });
  }

  private onActionWorkforcePlan(url: string) {
    const [_, action] = this.actionDetails;
    const modalRef = this._openWorkforcePlanDrawerImmediate(action);

    this._workforcePlanService.getWorkforcePlanByResourceUrl(url).subscribe({
      next: response => {
        try {
          if (!response.movementRequestParent) {
            modalRef.close();
            return;
          }

          const solicitation = WorkforcePlanSolicitationModel.create(
            response.movementRequestParent
          );
          const solicitationDetails = WorkforcePlanSolicitationDetailsModel.create(response);
          const rejectedIndex = solicitation.steps.findIndex(f => f.status === 'REJECTED');
          if (rejectedIndex >= 0)
            solicitation.steps = solicitation.steps.slice(0, rejectedIndex + 1);

          const employee = solicitation.employeeId
            ? { id: solicitation.employeeId, status: 'ACTIVE' }
            : null;

          const data = {
            type: solicitation.movementTypeEnumerator,
            mode: 'details',
            employee,
            companyId: action.companyId,
            solicitation,
            solicitationDetails,
            workforcePlanId: solicitation.workforcePlanId,
            drawerOpened: true,
            workScaleGroup: solicitation.workScaleGroup,
          };

          modalRef.componentInstance.data = data;
        } catch (error) {
          this._toasterService.showError('Falha ao abrir detalhes da solicitação.');
          modalRef.close();
        }
      },
      error: () => {
        this._toasterService.showError('Falha ao abrir detalhes da solicitação.');
        modalRef.close();
      },
    });
  }

  /** Abre o drawer imediatamente (com loading); os dados são atribuídos depois quando a API retornar. */
  private _openWorkforcePlanDrawerImmediate(action: ActionRequiredModel) {
    const modalRef = this._modalService.open(AppSharedDrawerWorkforceplanSolicitationComponent, {
      animation: false,
      backdrop: 'static',
      keyboard: true,
      size: 'xl',
      windowClass: 'wfp-modal',
      backdropClass: 'wfp-modal-backdrop',
    });

    modalRef.componentInstance.data = null;
    modalRef.componentInstance.source = 'action-required';

    modalRef.componentInstance.handleClose.subscribe(() => modalRef.close());
    modalRef.componentInstance.onSuccess.subscribe(() => {
      this._actionRequired.markActionRequiredForFinished(action.id).subscribe({
        next: () => {
          this.onListUpdated.emit();
          this._toasterService.showSuccess('Central de ações atualizada com sucesso');
          modalRef.close();
        },
        error: err => {
          this._toasterService.showError(
            'Não foi possível marcar a ação como finalizada. Tente novamente.'
          );
        },
      });
    });

    return modalRef;
  }
}
